/**
 * Operator diagnostics — pure mapping functions.
 *
 * Derives a compact diagnostic summary and single support-state status line
 * from existing scan outcome + readiness data. No I/O, fully deterministic.
 */

import type { ScanVerdict, ScanOutcomeSummary } from "../api/product-types.js";

// ── Support state (single status line) ──────────────────────────────────────

export type SupportState =
  | "ready"
  | "scan_limited"
  | "site_blocking"
  | "partial_data"
  | "no_data"
  | "setup_incomplete";

export interface SupportStateResult {
  state: SupportState;
  label: string;
  tone: "success" | "warning" | "danger" | "muted";
}

/**
 * Derive a single support-state status line from scan outcome + feature readiness.
 *
 * Priority order (highest wins):
 *   1. no_data — nothing to show
 *   2. site_blocking — scanner was blocked
 *   3. partial_data — scan succeeded but with gaps
 *   4. scan_limited — page cap reduced coverage
 *   5. setup_incomplete — degraded features
 *   6. ready — everything looks good
 */
export function deriveSupportState(
  verdict: ScanVerdict | null,
  outcome: ScanOutcomeSummary | null,
  degradedFeatures: string[],
): SupportStateResult {
  if (!verdict || !outcome) {
    if (degradedFeatures.length > 0) {
      return {
        state: "setup_incomplete",
        label: "Feature setup incomplete",
        tone: "warning",
      };
    }
    return { state: "ready", label: "Ready to scan", tone: "success" };
  }

  if (verdict === "no_data") {
    return { state: "no_data", label: "No scan data available", tone: "muted" };
  }

  if (verdict === "blocked") {
    return {
      state: "site_blocking",
      label: "Site blocking scanner",
      tone: "danger",
    };
  }

  if (verdict === "partial_data") {
    if (outcome.pagesBlocked > 0 && outcome.pagesScanned > 0) {
      return {
        state: "site_blocking",
        label: "Site partially blocking scanner",
        tone: "warning",
      };
    }
    if (outcome.pageCapApplied) {
      return {
        state: "scan_limited",
        label: "Scan limited by plan",
        tone: "warning",
      };
    }
    return {
      state: "partial_data",
      label: "Partial data only",
      tone: "warning",
    };
  }

  if (degradedFeatures.length > 0) {
    return {
      state: "setup_incomplete",
      label: "Feature setup incomplete",
      tone: "warning",
    };
  }

  return { state: "ready", label: "Ready to scan", tone: "success" };
}

// ── Diagnostic panel data ───────────────────────────────────────────────────

export interface DiagnosticLine {
  label: string;
  value: string;
  tone: "success" | "warning" | "danger" | "muted";
}

export interface DiagnosticSummary {
  supportState: SupportStateResult;
  lines: DiagnosticLine[];
  footnote: string;
}

/**
 * Build the full diagnostic summary from scan outcome data.
 * Returns structured lines suitable for rendering in a compact panel.
 */
export function buildDiagnosticSummary(
  outcome: ScanOutcomeSummary | null,
  screenshotsAvailable: boolean,
  degradedFeatures: string[],
): DiagnosticSummary {
  if (!outcome) {
    const supportState = deriveSupportState(null, null, degradedFeatures);
    const lines: DiagnosticLine[] = [];
    if (degradedFeatures.length > 0) {
      for (const f of degradedFeatures) {
        lines.push({ label: "Feature", value: f, tone: "warning" });
      }
    }
    return {
      supportState,
      lines,
      footnote: "Run a scan to see diagnostics.",
    };
  }

  const supportState = deriveSupportState(
    outcome.verdict,
    outcome,
    degradedFeatures,
  );
  const lines: DiagnosticLine[] = [];

  // Verdict
  lines.push({
    label: "Scan result",
    value: verdictToPlainEnglish(outcome.verdict),
    tone: verdictTone(outcome.verdict),
  });

  // Pages scanned / discovered
  lines.push({
    label: "Pages scanned",
    value: `${outcome.pagesScanned} of ${outcome.pagesDiscovered} discovered`,
    tone:
      outcome.pagesScanned === 0 && outcome.pagesDiscovered > 0
        ? "danger"
        : outcome.pagesScanned < outcome.pagesDiscovered
          ? "warning"
          : "success",
  });

  // Blocked pages
  if (outcome.pagesBlocked > 0) {
    lines.push({
      label: "Blocked pages",
      value: `${outcome.pagesBlocked} blocked by site security`,
      tone: "danger",
    });
  }

  // Failed pages
  if (outcome.pagesFailed > 0) {
    lines.push({
      label: "Failed pages",
      value: `${outcome.pagesFailed} failed to load`,
      tone: "danger",
    });
  }

  // Page cap
  if (outcome.pageCapApplied) {
    lines.push({
      label: "Page limit",
      value: "Plan page limit reduced this scan",
      tone: "warning",
    });
  }

  // Coverage
  lines.push({
    label: "Coverage",
    value: `${outcome.coveragePercent}%`,
    tone:
      outcome.coveragePercent >= 80
        ? "success"
        : outcome.coveragePercent >= 50
          ? "warning"
          : outcome.coveragePercent > 0
            ? "danger"
            : "muted",
  });

  // Screenshots
  lines.push({
    label: "Visual evidence",
    value: screenshotsAvailable
      ? "Available"
      : "Not available (storage not configured)",
    tone: screenshotsAvailable ? "success" : "muted",
  });

  // Degraded features
  for (const f of degradedFeatures) {
    lines.push({ label: "Feature notice", value: f, tone: "warning" });
  }

  // Footnote — honest about what this means
  const footnote = buildFootnote(outcome);

  return { supportState, lines, footnote };
}

// ── Internal helpers ────────────────────────────────────────────────────────

function verdictToPlainEnglish(verdict: ScanVerdict): string {
  switch (verdict) {
    case "clean":
      return "No automated issues found";
    case "issues_found":
      return "Accessibility issues detected";
    case "partial_data":
      return "Partial scan — some pages could not be tested";
    case "blocked":
      return "Scanner was blocked by site security";
    case "no_data":
      return "No pages could be scanned";
  }
}

function verdictTone(
  verdict: ScanVerdict,
): "success" | "warning" | "danger" | "muted" {
  switch (verdict) {
    case "clean":
      return "success";
    case "issues_found":
      return "danger";
    case "partial_data":
      return "warning";
    case "blocked":
      return "danger";
    case "no_data":
      return "muted";
  }
}

function buildFootnote(outcome: ScanOutcomeSummary): string {
  if (outcome.verdict === "no_data") {
    return "No pages were analysed. Check your site URL and security settings.";
  }
  if (outcome.verdict === "blocked") {
    return "Your site's security blocked the scanner. See Settings for allowlisting guidance.";
  }
  if (
    outcome.pagesScanned > 0 &&
    outcome.pagesScanned < outcome.pagesDiscovered
  ) {
    return "Not all discovered pages were tested. The scanner samples representative pages from each template group.";
  }
  if (outcome.verdict === "clean") {
    return "Automated checks found no issues, but manual review is still recommended for complete accessibility coverage.";
  }
  return "Automated checks identify common issues. Manual review is recommended for complete accessibility coverage.";
}

// ── Readiness feature translations ──────────────────────────────────────────

export interface FeatureReadinessState {
  status: "ready" | "degraded" | "disabled";
  reason: string;
}

/**
 * Translate machine-safe feature readiness reasons into user-facing language.
 * Returns empty array if no features are degraded/disabled.
 */
export function translateDegradedFeatures(features: {
  screenshots?: FeatureReadinessState;
  email?: FeatureReadinessState;
  billing?: FeatureReadinessState;
}): string[] {
  const result: string[] = [];

  if (features.screenshots?.status === "degraded") {
    if (features.screenshots.reason === "R2_PARTIAL_CONFIG") {
      result.push("Screenshot storage is partially configured");
    } else {
      result.push("Screenshots use local storage only");
    }
  }

  if (features.email?.status === "degraded") {
    result.push("Email notifications are not fully configured");
  }

  if (features.billing?.status === "degraded") {
    result.push(
      "Billing webhook is not configured — subscription changes may not sync",
    );
  }

  return result;
}
